'use client'
import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Link from 'next/link';
import Image from 'next/image';

export default function Home() {
  return (
    <main className="min-h-screen flex flex-col">
      {/* Hero Section */}
      <div className="relative h-screen">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <Image
            src="/images/hero-bg.jpg"
            alt="Fisioterapia com Raquel Luqueci"
            fill
            style={{ objectFit: 'cover' }}
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-r from-teal-900/70 to-teal-700/50"></div>
        </div>

        {/* Navbar */}
        <Navbar transparent={true} />

        {/* Hero Content */}
        <div className="relative z-10 flex items-center justify-center h-full px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white max-w-3xl">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              Fisioterapia Especializada para sua Saúde e Bem-estar
            </h1>
            <p className="text-xl sm:text-2xl mb-8">
              Reabilitação neurológica e física com abordagem personalizada para recuperação eficaz e qualidade de vida.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/agenda"
                className="px-8 py-3 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-md transition-colors duration-300"
              >
                Agendar Consulta
              </Link>
              <Link
                href="/sobre"
                className="px-8 py-3 bg-white hover:bg-gray-100 text-teal-800 font-medium rounded-md transition-colors duration-300"
              >
                Conhecer Raquel
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Serviços Especializados</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Oferecemos tratamentos personalizados para diversas condições, com foco em reabilitação neurológica e física.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Service 1 */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform duration-300 hover:transform hover:scale-105">
              <div className="h-48 bg-teal-600"></div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Avaliação Inicial</h3>
                <p className="text-gray-600 mb-4">
                  Avaliação completa para identificar suas necessidades específicas e desenvolver um plano de tratamento personalizado.
                </p>
                <Link
                  href="/servicos#avaliacao"
                  className="text-teal-600 hover:text-teal-800 font-medium"
                >
                  Saiba mais →
                </Link>
              </div>
            </div>

            {/* Service 2 */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform duration-300 hover:transform hover:scale-105">
              <div className="h-48 bg-teal-600"></div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Sessão Individual</h3>
                <p className="text-gray-600 mb-4">
                  Atendimento personalizado com técnicas avançadas de fisioterapia para maximizar sua recuperação e bem-estar.
                </p>
                <Link
                  href="/servicos#sessao-individual"
                  className="text-teal-600 hover:text-teal-800 font-medium"
                >
                  Saiba mais →
                </Link>
              </div>
            </div>

            {/* Service 3 */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform duration-300 hover:transform hover:scale-105">
              <div className="h-48 bg-teal-600"></div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Pacotes com Desconto</h3>
                <p className="text-gray-600 mb-4">
                  Pacotes de sessões com valores especiais para tratamentos contínuos, garantindo economia e resultados consistentes.
                </p>
                <Link
                  href="/servicos#pacotes"
                  className="text-teal-600 hover:text-teal-800 font-medium"
                >
                  Saiba mais →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Sobre Raquel Luqueci</h2>
              <p className="text-lg text-gray-600 mb-4">
                Fisioterapeuta especializada em reabilitação neurológica, com formação pela Escola Superior de Saúde do Alcoitão (ESSAlcoitão) e pós-graduação em demência.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Com anos de experiência clínica, Raquel dedica-se a proporcionar tratamentos personalizados que melhoram significativamente a qualidade de vida de seus pacientes.
              </p>
              <Link
                href="/sobre"
                className="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-md transition-colors duration-300 inline-block"
              >
                Conheça minha trajetória
              </Link>
            </div>
            <div className="relative h-96 rounded-lg overflow-hidden shadow-xl">
              <div className="absolute inset-0 bg-teal-600"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Depoimentos</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Veja o que nossos pacientes dizem sobre os resultados obtidos com nossos tratamentos.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center mb-4">
                <div className="h-12 w-12 rounded-full bg-teal-200 flex items-center justify-center text-teal-700 font-bold text-xl">
                  M
                </div>
                <div className="ml-4">
                  <h4 className="font-semibold">Maria Silva</h4>
                  <p className="text-gray-500 text-sm">Paciente de Reabilitação Neurológica</p>
                </div>
              </div>
              <p className="text-gray-600">
                "Após meu AVC, pensei que nunca mais conseguiria realizar atividades simples do dia a dia. Com o tratamento da Raquel, recuperei minha independência e qualidade de vida."
              </p>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center mb-4">
                <div className="h-12 w-12 rounded-full bg-teal-200 flex items-center justify-center text-teal-700 font-bold text-xl">
                  J
                </div>
                <div className="ml-4">
                  <h4 className="font-semibold">João Santos</h4>
                  <p className="text-gray-500 text-sm">Paciente de Reabilitação Física</p>
                </div>
              </div>
              <p className="text-gray-600">
                "A abordagem personalizada da Raquel fez toda a diferença na minha recuperação. Sua dedicação e conhecimento técnico são impressionantes."
              </p>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center mb-4">
                <div className="h-12 w-12 rounded-full bg-teal-200 flex items-center justify-center text-teal-700 font-bold text-xl">
                  A
                </div>
                <div className="ml-4">
                  <h4 className="font-semibold">Ana Oliveira</h4>
                  <p className="text-gray-500 text-sm">Familiar de Paciente</p>
                </div>
              </div>
              <p className="text-gray-600">
                "Ver a evolução do meu pai após iniciar o tratamento com a Raquel foi emocionante. Ela não apenas trata o paciente, mas também orienta a família de forma excepcional."
              </p>
            </div>
          </div>

          <div className="text-center mt-8">
            <Link
              href="/depoimentos"
              className="text-teal-600 hover:text-teal-800 font-medium"
            >
              Ver mais depoimentos →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-teal-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Pronto para iniciar sua jornada de recuperação?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Agende sua avaliação inicial e descubra como nossos tratamentos personalizados podem ajudar você a recuperar sua saúde e bem-estar.
          </p>
          <Link
            href="/agenda"
            className="px-8 py-3 bg-white hover:bg-gray-100 text-teal-800 font-medium rounded-md transition-colors duration-300 inline-block"
          >
            Agendar Consulta
          </Link>
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Blog</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Dicas, informações e novidades sobre fisioterapia, saúde e bem-estar.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Blog Post 1 */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="h-48 bg-gray-200"></div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Exercícios para melhorar a mobilidade após AVC
                </h3>
                <p className="text-gray-500 text-sm mb-3">12 de Maio, 2025</p>
                <p className="text-gray-600 mb-4">
                  Conheça exercícios simples que podem ser realizados em casa para complementar o tratamento de fisioterapia após um AVC.
                </p>
                <Link
                  href="/blog/exercicios-mobilidade-avc"
                  className="text-teal-600 hover:text-teal-800 font-medium"
                >
                  Ler mais →
                </Link>
              </div>
            </div>

            {/* Blog Post 2 */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="h-48 bg-gray-200"></div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  A importância da fisioterapia na doença de Parkinson
                </h3>
                <p className="text-gray-500 text-sm mb-3">5 de Maio, 2025</p>
                <p className="text-gray-600 mb-4">
                  Descubra como a fisioterapia pode ajudar a gerenciar os sintomas e melhorar a qualidade de vida de pacientes com Parkinson.
                </p>
                <Link
                  href="/blog/fisioterapia-parkinson"
                  className="text-teal-600 hover:text-teal-800 font-medium"
                >
                  Ler mais →
                </Link>
              </div>
            </div>

            {/* Blog Post 3 */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="h-48 bg-gray-200"></div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Dicas para cuidadores de pacientes neurológicos
                </h3>
                <p className="text-gray-500 text-sm mb-3">28 de Abril, 2025</p>
                <p className="text-gray-600 mb-4">
                  Orientações práticas para familiares e cuidadores que auxiliam pacientes com condições neurológicas em casa.
                </p>
                <Link
                  href="/blog/dicas-cuidadores"
                  className="text-teal-600 hover:text-teal-800 font-medium"
                >
                  Ler mais →
                </Link>
              </div>
            </div>
          </div>

          <div className="text-center mt-8">
            <Link
              href="/blog"
              className="text-teal-600 hover:text-teal-800 font-medium"
            >
              Ver todos os artigos →
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Entre em Contato</h2>
              <p className="text-lg text-gray-600 mb-8">
                Tem dúvidas ou precisa de mais informações? Entre em contato conosco e teremos prazer em ajudar.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 text-teal-600 mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div className="ml-3 text-gray-600">
                    <p>Lisboa, Portugal</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 text-teal-600 mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div className="ml-3 text-gray-600">
                    <p>
                      <a href="tel:+351912345678" className="hover:text-teal-600">
                        +351 912 345 678
                      </a>
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 text-teal-600 mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="ml-3 text-gray-600">
                    <p>
                      <a href="mailto:contato@raquelluqueci.com" className="hover:text-teal-600">
                        contato@raquelluqueci.com
                      </a>
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8">
                <Link
                  href="/contato"
                  className="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-md transition-colors duration-300 inline-block"
                >
                  Formulário de Contato
                </Link>
              </div>
            </div>
            
            <div className="relative h-96 rounded-lg overflow-hidden shadow-xl">
              <div className="absolute inset-0 bg-gray-300"></div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
